/**
 * TesteCoranteForm.jsx
 * Formulário para "Testes de Corante / Inspeção de Ramal" (SABESP).
 *
 * Props:
 *   value    → objeto com todos os dados do formulário
 *   onChange → (novoValor) => void
 */

import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import {
  Box, Flex, Text, Heading, Button, IconButton, Icon,
  SimpleGrid, Input, Checkbox, Stack, Collapse,
  Image, Divider, Badge,
  Modal, ModalOverlay, ModalContent, ModalBody,
  useColorModeValue, useDisclosure,
  Menu, MenuButton, MenuList, MenuItem,
} from '@chakra-ui/react';
import { MdCameraAlt, MdClose, MdExpandMore, MdExpandLess, MdDelete, MdAdd, MdDownload } from 'react-icons/md';
import Card from 'components/card/Card.js';

// ─── Opções ───────────────────────────────────────────────────────────────────
const OPT_CATEGORIA = ['Residência', 'Comércio', 'Indústria', 'Pública', 'Mista'];

// Forma de Esgotamento — agrupada por situação (conforme formulário oficial SABESP)
const FORMAS_ESGOTAMENTO = {
  'LIGADO à RCE': [
    { cod: '01', desc: 'LIGADO por mais de um ramal' },
    { cod: '02', desc: 'LIGADO pela frente' },
    { cod: '03', desc: 'LIGADO pela lateral' },
    { cod: '04', desc: 'LIGADO pelo fundo' },
    { cod: '05', desc: 'LIGADO por autorização de passagem pelo fundo' },
    { cod: '06', desc: 'LIGADO por autorização de passagem pela lateral' },
    { cod: '07', desc: 'LIGADO por ramal coletivo' },
    { cod: '',   desc: 'Águas Pluviais conectado ao esgoto' },
  ],
  'FACTÍVEL': [
    { cod: '21', desc: 'FACTÍVEL pela frente – esgotando em fossa' },
    { cod: '22', desc: 'FACTÍVEL pela lateral – esgotando em fossa' },
    { cod: '23', desc: 'FACTÍVEL pelo fundo – esgotando em fossa' },
    { cod: '24', desc: 'FACTÍVEL pela frente – esgotando em rede particular' },
    { cod: '25', desc: 'FACTÍVEL pelo fundo – esgotando em rede particular' },
    { cod: '26', desc: 'FACTÍVEL pela lateral – esgotando em rede particular' },
    { cod: '27', desc: 'FACTÍVEL pela frente – esgotando em córrego / GAP / céu aberto' },
    { cod: '28', desc: 'FACTÍVEL pela lateral – esgotando em córrego / GAP / céu aberto' },
    { cod: '29', desc: 'FACTÍVEL pelo fundo – esgotando em córrego / GAP / céu aberto' },
    { cod: '33', desc: 'FACTÍVEL – autorização de passagem pela lateral - esgotando em fossa' },
    { cod: '34', desc: 'FACTÍVEL – autorização de passagem pela lateral - esgotando em rede particular' },
    { cod: '35', desc: 'FACTÍVEL – autorização de passagem pela lateral - esgotando em córrego / GAP / céu aberto' },
    { cod: '36', desc: 'FACTÍVEL – autorização de passagem pelo fundo - esgotando em fossa' },
    { cod: '37', desc: 'FACTÍVEL – autorização de passagem pelo fundo - esgotando em rede particular' },
    { cod: '38', desc: 'FACTÍVEL – autorização de passagem pelo fundo - esgotando em córrego / GAP / céu aberto' },
    { cod: '42', desc: 'FACTÍVEL – imóvel em construção' },
  ],
  'POTENCIAL': [
    { cod: '51', desc: 'POTENCIAL – esgotando em fossa' },
    { cod: '52', desc: 'POTENCIAL – esgotando em rede particular' },
    { cod: '53', desc: 'POTENCIAL – esgotando em córrego / GAP / céu aberto' },
    { cod: '54', desc: 'POTENCIAL – sistema de esgotamento e tratamento particular' },
    { cod: '55', desc: 'POTENCIAL – habitação subnormal' },
  ],
  'FACTÍVEL POR BOMBEAMENTO': [
    { cod: '61', desc: 'FACTÍVEL por bombeamento – esgotando em fossa' },
    { cod: '62', desc: 'FACTÍVEL por bombeamento – esgotando em rede particular' },
    { cod: '63', desc: 'FACTÍVEL por bombeamento – esgotando em córrego / GAP / céu aberto' },
    { cod: '67', desc: 'FACTÍVEL por bombeamento – sistema de esgotamento e tratamento particular' },
    { cod: '69', desc: 'FACTÍVEL por bombeamento – ligação subnormal' },
  ],
};

const GROUP_COLORS = {
  'LIGADO à RCE':           'green',
  'FACTÍVEL':               'blue',
  'POTENCIAL':              'orange',
  'FACTÍVEL POR BOMBEAMENTO': 'purple',
};


// ─── Valor padrão ─────────────────────────────────────────────────────────────
export const CORANTE_PADRAO = () => ({
  // Identificação
  enderecoImovel:   '',
  rgi:              '',
  codificacao:      '',
  hidrometro:       '',
  leitura:          '',
  moradores:        '',
  tl:               '',
  categoriaUso:     '',
  proprietario:     '',   // 'Sim' | 'Não'
  nomeCliente:      '',
  rg:               '',
  telefone:         '',

  // Forma de esgotamento
  formaEsgotamento: [],   // códigos selecionados, ex: ['02', '07']

  // Informações adicionais (checkboxes)
  estudoProlongamento:       false,
  estudoProfundidadeSoleira: false,
  imovelFechado:             false,
  clienteNaoAutorizou:       false,
  pvObstruido:               false,
  redeObstruida:             false,
  clienteConectadoRamal:     false,
  clienteDesejaLigacao:      false,
  pdeJaCadastrado:           false,
  imovelNaoLocalizado:       false,

  // Assinatura do cliente (base64 PNG)
  assinaturaCliente:     null,
  clienteRecusouAssinar: false,

  // Fotos
  fotos: [],  // [{ file, preview, url, legenda }]
});

// ─── helpers ──────────────────────────────────────────────────────────────────
function SLabel({ children, required }) {
  const c = useColorModeValue('gray.600', 'gray.300');
  return (
    <Text fontSize="11px" fontWeight="700" textTransform="uppercase"
      letterSpacing=".04em" color={c} mb="4px">
      {children}{required && <Text as="span" color="red.400" ms="1">*</Text>}
    </Text>
  );
}

function SectionTitle({ children }) {
  const c = useColorModeValue('navy.700', 'white');
  return (
    <Text fontWeight="800" fontSize="xs" color={c}
      textTransform="uppercase" letterSpacing=".06em" mb="10px">
      {children}
    </Text>
  );
}

function MInput({ inputBg, ...props }) {
  return (
    <Input bg={inputBg} borderRadius="8px"
      fontSize={{ base: '16px', md: '14px' }}
      h={{ base: '44px', md: '36px' }}
      {...props} />
  );
}
// ─── Componente Principal ───────────────────────────────────────────────────────
export default function TesteCoranteForm({ value, onChange }) {
  const inputBg   = useColorModeValue('white', 'whiteAlpha.100');
  const cardBg    = useColorModeValue('white', 'navy.800');
  const borderCol = useColorModeValue('gray.100', 'gray.600');
  const subText   = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('navy.700', 'white');
  const infoBg    = useColorModeValue('gray.50', 'navy.900');
  const fotoRef    = useRef(); // galeria (sem capture)
  const cameraRef  = useRef(); // câmera (capture="environment")
  const sigRef     = useRef();
  const { isOpen: isSigOpen, onOpen: onSigOpen, onClose: onSigClose } = useDisclosure();

  const data = value || CORANTE_PADRAO();
  const set   = (field, val) => onChange({ ...data, [field]: val });

  const handleConfirmarAssinatura = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      set('assinaturaCliente', sigRef.current.toDataURL('image/png'));
    }
    onSigClose();
  };

  const handleAbrirModal = () => {
    onSigOpen();
    // Dois frames garantem que o modal terminou de animar e o canvas
    // tem as dimensões corretas antes de limpar (100ms é insuficiente em celulares lentos)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        sigRef.current?.clear();
      });
    });
  };

  // fotos
  const addFotos = (e) => {
    const novas = Array.from(e.target.files).map(f => ({
      file: f, preview: URL.createObjectURL(f), url: null, legenda: '',
    }));
    set('fotos', [...(data.fotos || []), ...novas]);
    e.target.value = '';
  };
  const removeFoto  = (i) => {
    const f = (data.fotos || [])[i];
    // Revoga blob URL para liberar memória (evita acumular no celular)
    if (f?.preview?.startsWith('blob:')) URL.revokeObjectURL(f.preview);
    set('fotos', (data.fotos || []).filter((_, j) => j !== i));
  };
  const setLegenda  = (i, leg) =>
    set('fotos', (data.fotos || []).map((f, j) => j === i ? { ...f, legenda: leg } : f));

  const downloadFoto = async (src, idx) => {
    if (!src) return;
    const nome = `foto_${idx + 1}.jpg`;
    if (src.startsWith('blob:') || src.startsWith('data:')) {
      const a = document.createElement('a');
      a.href = src; a.download = nome;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      return;
    }
    try {
      const resp = await fetch(src, { mode: 'cors', credentials: 'omit' });
      if (!resp.ok) throw new Error('fetch failed');
      const blob   = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = nome;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
    } catch {
      window.open(src, '_blank');
    }
  };

  // estado de abertura de cada grupo (todos fechados por padrão)
  const [openGroups, setOpenGroups] = useState(
    Object.fromEntries(Object.keys(FORMAS_ESGOTAMENTO).map(g => [g, false]))
  );
  const toggleGroup = (g) => setOpenGroups(prev => ({ ...prev, [g]: !prev[g] }));

  return (
    <Stack spacing="20px">

      {/* ══ IDENTIFICAÇÃO DO IMÓVEL ══════════════════════════════════════════ */}
      <Card bg={cardBg} p={{ base: '14px', md: '18px' }} borderWidth="1px" borderColor={borderCol}>
        <Heading size="xs" color={textColor} textTransform="uppercase"
          letterSpacing=".06em" mb="14px">
          Identificação do Imóvel
        </Heading>

        {/* Endereço — linha inteira */}
        <Box mb="12px">
          <SLabel required>Endereço do Imóvel</SLabel>
          <MInput inputBg={inputBg} placeholder="Av. Maria Cursi, 1056 BC Jornal..."
            value={data.enderecoImovel || ''}
            onChange={(e) => set('enderecoImovel', e.target.value)} />
        </Box>

        {/* RGI + Codificação */}
        <SimpleGrid columns={{ base: 1, sm: 2 }} gap="10px" mb="12px">
          <Box>
            <SLabel>RGI</SLabel>
            <MInput inputBg={inputBg} placeholder="Ex: 813445388"
              inputMode="numeric"
              value={data.rgi || ''}
              onChange={(e) => set('rgi', e.target.value)} />
          </Box>
          <Box>
            <SLabel>Codificação</SLabel>
            <MInput inputBg={inputBg} placeholder="Ex: Y12T519200"
              inputMode="numeric"
              value={data.codificacao || ''}
              onChange={(e) => set('codificacao', e.target.value)} />
          </Box>
        </SimpleGrid>

        {/* Hidrômetro + Leitura + Moradores + TL */}
        <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} gap="10px" mb="14px">
          <Box>
            <SLabel>Hidrômetro</SLabel>
            <MInput inputBg={inputBg} placeholder="Nº"
              value={data.hidrometro || ''}
              onChange={(e) => set('hidrometro', e.target.value)} />
          </Box>
          <Box>
            <SLabel>Leitura (m³)</SLabel>
            <MInput inputBg={inputBg} type="number" min="0" placeholder="0"
              inputMode="decimal"
              value={data.leitura || ''}
              onChange={(e) => set('leitura', e.target.value)} />
          </Box>
          <Box>
            <SLabel>Moradores</SLabel>
            <MInput inputBg={inputBg} type="number" min="0" placeholder="0"
              inputMode="numeric"
              value={data.moradores || ''}
              onChange={(e) => set('moradores', e.target.value)} />
          </Box>
          <Box>
            <SLabel>TL</SLabel>
            <MInput inputBg={inputBg} placeholder="TL..."
              value={data.tl || ''}
              onChange={(e) => set('tl', e.target.value)} />
          </Box>
        </SimpleGrid>

        <Divider mb="14px" />

        {/* Categoria de Uso */}
        <SectionTitle>Categoria de Uso do Imóvel</SectionTitle>
        <SimpleGrid columns={{ base: 2, sm: 3, md: 5 }} gap="8px" mb="12px">
          {OPT_CATEGORIA.map(cat => (
            <Button
              key={cat}
              size="md"
              h={{ base: '44px', md: '36px' }}
              borderRadius="8px"
              fontSize={{ base: '14px', md: 'sm' }}
              colorScheme={data.categoriaUso === cat ? 'brand' : 'gray'}
              variant={data.categoriaUso === cat ? 'solid' : 'outline'}
              onClick={() => set('categoriaUso', data.categoriaUso === cat ? '' : cat)}
            >
              {cat}
            </Button>
          ))}
        </SimpleGrid>
        {/* Proprietário */}
        <Box mb="4px">
          <SLabel>Proprietário do Imóvel?</SLabel>
          <Flex gap="8px">
            {['Sim', 'Não'].map(op => (
              <Button key={op} size="sm" h="36px" borderRadius="8px" minW="72px"
                colorScheme={data.proprietario === op ? 'brand' : 'gray'}
                variant={data.proprietario === op ? 'solid' : 'outline'}
                onClick={() => set('proprietario', data.proprietario === op ? '' : op)}>
                {op}
              </Button>
            ))}
          </Flex>
        </Box>      </Card>

      {/* ══ CONTATO DO CLIENTE ═══════════════════════════════════════════════ */}
      <Card bg={cardBg} p={{ base: '14px', md: '18px' }} borderWidth="1px" borderColor={borderCol}>
        <Heading size="xs" color={textColor} textTransform="uppercase"
          letterSpacing=".06em" mb="14px">
          Dados do Cliente
        </Heading>
        <SimpleGrid columns={{ base: 1, sm: 2 }} gap="10px">
          <Box gridColumn={{ sm: '1 / -1' }}>
            <SLabel>Nome do Cliente</SLabel>
            <MInput inputBg={inputBg} placeholder="Nome completo"
              value={data.nomeCliente || ''}
              onChange={(e) => set('nomeCliente', e.target.value)} />
          </Box>
          <Box>
            <SLabel>RG</SLabel>
            <MInput inputBg={inputBg} placeholder="RG do cliente"
              inputMode="numeric"
              value={data.rg || ''}
              onChange={(e) => set('rg', e.target.value)} />
          </Box>
          <Box>
            <SLabel>Telefone</SLabel>
            <MInput inputBg={inputBg} placeholder="(11) 99999-0000" type="tel"
              value={data.telefone || ''}
              onChange={(e) => set('telefone', e.target.value)} />
          </Box>
        </SimpleGrid>
      </Card>

      {/* ══ FORMA DE ESGOTAMENTO ═════════════════════════════════════════════ */}
      <Card bg={cardBg} p={{ base: '14px', md: '18px' }} borderWidth="1px" borderColor={borderCol}>
        <Heading size="xs" color={textColor} textTransform="uppercase"
          letterSpacing=".06em" mb="6px">
          Forma de Esgotamento
        </Heading>
        <Text fontSize="11px" color={subText} mb="14px">
          Posição do imóvel em relação à Rede Coletora de Esgotos (RCE).
        </Text>

        <Stack spacing="8px" mb="16px">
          {Object.entries(FORMAS_ESGOTAMENTO).map(([grupo, itens]) => {
            const isOpen       = openGroups[grupo];
            const groupColor   = GROUP_COLORS[grupo] || 'gray';
            const selecionados = itens.filter(o => (data.formaEsgotamento || []).includes(o.cod));
            return (
              <Box key={grupo} borderRadius="10px" overflow="hidden"
                border="1px solid" borderColor={selecionados.length > 0 ? `${groupColor}.300` : borderCol}>

                {/* cabeçalho do grupo — clique abre/fecha */}
                <Flex
                  align="center" justify="space-between"
                  p="10px 12px" cursor="pointer"
                  bg={infoBg}
                  onClick={() => toggleGroup(grupo)}
                  _hover={{ opacity: 0.82 }}
                  userSelect="none"
                >
                  <Flex align="center" gap="8px" wrap="wrap">
                    <Badge colorScheme={groupColor} borderRadius="full"
                      px="3" fontSize="11px" fontWeight="800">
                      {grupo}
                    </Badge>
                    <Text fontSize="11px" color={subText}>
                      {itens.length} {itens.length === 1 ? 'opção' : 'opções'}
                    </Text>
                    {/* mostra os itens selecionados quando o grupo está fechado */}
                    {selecionados.length > 0 && !isOpen && selecionados.map(s => (
                      <Badge key={s.cod} colorScheme={groupColor} variant="subtle"
                        borderRadius="full" px="2" fontSize="11px">
                        ✓ {s.cod}
                      </Badge>
                    ))}
                  </Flex>
                  <Icon
                    as={isOpen ? MdExpandLess : MdExpandMore}
                    boxSize="20px" color={subText} flexShrink="0"
                  />
                </Flex>

                {/* lista de itens */}
                <Collapse in={isOpen} animateOpacity>
                  <Stack spacing="0" px="12px" py="6px"
                    divider={<Divider />}>
                    {itens.map(({ cod, desc }) => {
                      const checked = (data.formaEsgotamento || []).includes(cod);
                      const toggleCod = () => {
                        const arr = data.formaEsgotamento || [];
                        set('formaEsgotamento', checked
                          ? arr.filter(c => c !== cod)
                          : [...arr, cod]
                        );
                      };
                      return (
                        <Checkbox
                          key={cod}
                          py="9px"
                          colorScheme={groupColor}
                          isChecked={checked}
                          onChange={toggleCod}
                          size="md"
                        >
                          <Flex align="baseline" gap="8px">
                            <Text fontSize="12px" fontWeight="800"
                              color={subText} w="22px" flexShrink="0">
                              {cod}
                            </Text>
                            <Text fontSize="13px" color={textColor} lineHeight="1.4">
                              {desc}
                            </Text>
                          </Flex>
                        </Checkbox>
                      );
                    })}
                  </Stack>
                </Collapse>
              </Box>
            );
          })}
        </Stack>

        <Divider mb="14px" />

        {/* Informações Adicionais */}
        <SectionTitle>Informações Adicionais</SectionTitle>
        <Stack spacing="10px">
          {[
            ['estudoProlongamento',       'Necessário Estudo de Prolongamento de RCE'],
            ['estudoProfundidadeSoleira', 'Necessário Estudo de Profundidade de Soleira'],
            ['imovelFechado',             'Imóvel fechado / Morador ausente'],
            ['clienteNaoAutorizou',       'Cliente não autorizou a entrada no imóvel'],
            ['pvObstruido',               'Poço de visita coberto'],
            ['redeObstruida',             'Rede ou caixa de inspeção (cliente) obstruída'],
            ['clienteConectadoRamal',     'Cliente conectado através de ramal coletivo'],
            ['clienteDesejaLigacao',      'Cliente deseja ligação / disponibilização de ramal'],
            ['pdeJaCadastrado',           'PDE já cadastrado com TL1'],
            ['imovelNaoLocalizado',        'IMÓVEL NÃO LOCALIZADO'],
          ].map(([field, label]) => (
            <Checkbox key={field}
              isChecked={!!data[field]}
              onChange={(e) => set(field, e.target.checked)}
              colorScheme="orange" size="lg">
              <Text fontSize="sm" color={textColor}>{label}</Text>
            </Checkbox>
          ))}
        </Stack>
      </Card>

      {/* ══ FOTOS ════════════════════════════════════════════════════════════ */}
      <Card bg={cardBg} p={{ base: '14px', md: '18px' }} borderWidth="1px" borderColor={borderCol}>
        <Heading size="xs" color={textColor} textTransform="uppercase"
          letterSpacing=".06em" mb="12px">
          Fotos / Evidências
        </Heading>
        {/* Câmera: capture="environment" → abre câmera diretamente */}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment"
          style={{ display: 'none' }} onChange={addFotos} />
        {/* Galeria: MIME types explícitos → abre seletor de arquivos do SO */}
        <input ref={fotoRef} type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
          multiple style={{ display: 'none' }} onChange={addFotos} />

        {(data.fotos || []).length === 0 && (
          <Text fontSize="sm" color={subText} textAlign="center" py="4">
            Nenhuma foto adicionada.
          </Text>
        )}

        <Stack spacing="10px" mb="12px">
          {(data.fotos || []).map((f, i) => (
            <Box key={i} borderRadius="10px" overflow="hidden"
              border="1px solid" borderColor={borderCol}>
              <Box position="relative">
                <Image src={f.preview || f.url} w="100%"
                  h={{ base: '160px', md: '120px' }} objectFit="cover" />
                <Flex position="absolute" top="6px" right="6px" gap="4px">
                  <IconButton icon={<Icon as={MdDownload} />} size="sm" colorScheme="blue"
                    borderRadius="full" aria-label="download"
                    onClick={() => downloadFoto(f.preview || f.url, i)} />
                  <IconButton icon={<Icon as={MdClose} />} size="sm" colorScheme="red"
                    borderRadius="full" aria-label="remover"
                    onClick={() => removeFoto(i)} />
                </Flex>
              </Box>
              <Box px="8px" py="8px">
                <Text fontSize="11px" color={subText} mb="4px">Foto {i + 1}</Text>
                <MInput inputBg={inputBg}
                  h={{ base: '40px', md: '32px' }}
                  fontSize={{ base: '16px', md: '13px' }}
                  placeholder="Legenda..." value={f.legenda || ''}
                  onChange={(e) => setLegenda(i, e.target.value)} />
              </Box>
            </Box>
          ))}
        </Stack>

        <Menu>
          <MenuButton
            as={Button}
            w="100%"
            size="md" h={{ base: '48px', md: '40px' }}
            variant="outline"
            colorScheme="brand" borderRadius="12px">
            <Flex align="center" justify="center" gap="8px">
              <Icon as={MdCameraAlt} boxSize="18px" />
              <Text>Adicionar foto</Text>
            </Flex>
          </MenuButton>
          <MenuList minW="160px">
            <MenuItem icon={<Icon as={MdCameraAlt} boxSize="18px" />}
              onClick={() => cameraRef.current?.click()}>
              Câmera
            </MenuItem>
            <MenuItem icon={<Icon as={MdAdd} boxSize="18px" />}
              onClick={() => fotoRef.current?.click()}>
              Galeria
            </MenuItem>
          </MenuList>
        </Menu>
      </Card>

      {/* ══ ASSINATURA DO CLIENTE ═════════════════════════════════════ */}
      <Card bg={cardBg} p={{ base: '14px', md: '18px' }} borderWidth="1px"
        borderColor={data.clienteRecusouAssinar ? 'red.400' : data.assinaturaCliente ? 'green.400' : borderCol}>
        <Heading size="xs" color={textColor} textTransform="uppercase"
          letterSpacing=".06em" mb="4px">
          Assinatura do Cliente
        </Heading>
        <Text fontSize="11px" color={subText} mb="14px">
          Recebi cópia de igual teor, com o qual concordo.
        </Text>

        {/* Prévia da assinatura (base64 ou URL salva) — oculta se recusou */}
        {!data.clienteRecusouAssinar && data.assinaturaCliente && (
          <Box mb="12px" borderWidth="1px" borderColor="green.300" borderRadius="10px"
            overflow="hidden" bg="white" p="4px">
            <Image
              src={data.assinaturaCliente}
              maxH="80px" w="100%" objectFit="contain"
            />
          </Box>
        )}

        {/* Mensagem de recusa */}
        {data.clienteRecusouAssinar && (
          <Box mb="12px" borderWidth="1.5px" borderColor="red.300" borderRadius="10px"
            bg="red.50" p="10px" textAlign="center">
            <Text fontSize="13px" color="red.600" fontWeight="700">
              Cliente recusou assinar
            </Text>
          </Box>
        )}

        {/* Botão de assinatura — oculto se recusou */}
        {!data.clienteRecusouAssinar && (
          <Flex gap="10px" align="center" wrap="wrap" mb="12px">
            <Button
              flex={1} minW="160px" size="md" h={{ base: '48px', md: '40px' }}
              colorScheme="blue" borderRadius="12px"
              onClick={handleAbrirModal}>
              {data.assinaturaCliente ? 'Refazer Assinatura' : 'Abrir para Assinar'}
            </Button>
            {data.assinaturaCliente ? (
              <>
                <Text fontSize="12px" color="green.500" fontWeight="600">
                  ✓ Assinatura coletada
                </Text>
                <IconButton size="sm" icon={<Icon as={MdDelete} />}
                  colorScheme="red" variant="ghost" aria-label="remover"
                  onClick={() => set('assinaturaCliente', null)} />
              </>
            ) : (
              <Text fontSize="12px" color={subText}>Nenhuma assinatura ainda.</Text>
            )}
          </Flex>
        )}

        {/* Checkbox de recusa */}
        <Checkbox
          isChecked={!!data.clienteRecusouAssinar}
          colorScheme="red"
          size="md"
          onChange={(e) => {
            const recusou = e.target.checked;
            onChange({ ...data, clienteRecusouAssinar: recusou, assinaturaCliente: recusou ? null : data.assinaturaCliente });
          }}
        >
          <Text fontSize="sm" color={data.clienteRecusouAssinar ? 'red.500' : textColor} fontWeight={data.clienteRecusouAssinar ? '600' : 'normal'}>
            Cliente recusou assinar
          </Text>
        </Checkbox>
      </Card>

      {/* ══ MODAL DE ASSINATURA ════════════════════════════════════════════ */}
      <Modal isOpen={isSigOpen} onClose={onSigClose} size="full" motionPreset="slideInBottom">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent
          m={0} borderRadius={0}
          display="flex" flexDirection="column"
          h="100dvh" maxH="100dvh"
          bg="white"
        >
          <ModalBody
            p="16px"
            display="flex" flexDirection="column"
            flex={1} overflow="hidden"
          >
            {/* Cabeçalho */}
            <Flex justify="space-between" align="center" mb="12px" flexShrink={0}>
              <Box>
                <Heading size="sm" color="navy.700">Assinatura do Cliente</Heading>
                <Text fontSize="11px" color="gray.500">
                  Recebi cópia de igual teor, com o qual concordo.
                </Text>
              </Box>
              <IconButton
                size="sm" icon={<Icon as={MdClose} />}
                variant="ghost" colorScheme="gray" aria-label="fechar"
                onClick={onSigClose}
              />
            </Flex>

            {/* Área de desenho — ocupa todo o espaço restante */}
            <Box
              flex={1} minH={0}
              borderWidth="2px" borderColor="gray.300" borderRadius="12px"
              overflow="hidden" bg="white" cursor="crosshair"
              mb="14px"
            >
              <SignatureCanvas
                ref={sigRef}
                penColor="#1a202c"
                canvasProps={{ style: { width: '100%', height: '100%', display: 'block' } }}
              />
            </Box>

            {/* Botões */}
            <Flex gap="10px" justify="flex-end" flexShrink={0}>
              <Button size="md" variant="outline" colorScheme="red" borderRadius="8px"
                leftIcon={<Icon as={MdDelete} />}
                onClick={() => sigRef.current?.clear()}>
                Limpar
              </Button>
              <Button size="md" colorScheme="green" borderRadius="8px"
                onClick={handleConfirmarAssinatura}>
                Confirmar Assinatura
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

    </Stack>
  );
}
